import moment from 'moment'
import { round } from 'lodash'

import { startDateFormatString } from '../js/constants'

export function calculateTotalLiters(measurements) {
  if (!measurements || !measurements.length) {
    return undefined
  }

  return measurements
    .filter(m => m && typeof m.volume === 'number')
    .map(m => m.volume)
    .reduce(sum, 0)
}

export function calculateAverageTemperature({ measurements }) {
  if (!measurements || !measurements.length) {
    return undefined
  }

  const temperatures = measurements.filter(m => m && typeof m.temperature === 'number').map(m => m.temperature)

  return temperatures.reduce(sum, 0) / temperatures.length
}

/**
 * Calculate the density of a measurement in kg/l
 * Returns null in case of invalid inputs
 *
 * @param {Object} values
 * @property {number} densityDuringLoading      Density in kg/l
 * @property {number} temperatureDuringLoading  Temperature in Celsius
 * @property {number} temperatureMeasurement    Temperature in Celsius
 */
export function calculateMeasurementDensity({
  densityDuringLoading,
  temperatureDuringLoading,
  temperatureMeasurement
} = {}) {
  if (
    typeof densityDuringLoading !== 'number' ||
    typeof temperatureDuringLoading !== 'number' ||
    typeof temperatureMeasurement !== 'number'
  ) {
    return null
  }

  return densityDuringLoading - 0.00068 * (temperatureMeasurement - temperatureDuringLoading)
}

/**
 * Calculate the mass based on volume and density (rocket science ;))
 * @param {Object} values
 * @property {number} volume
 * @property {number} density
 */
export function calculateMass({ volume, density } = {}) {
  if (typeof volume !== 'number' || typeof density !== 'number') {
    return null
  }

  return volume * density
}

/**
 *
 * @param {Object} values
 * @property {Array} measurements
 * @property {number} densityDuringLoading      Density in kg/l
 * @property {number} temperatureMeasurement    Temperature in Celsius
 */
export function calculateTotalMass({ measurements, densityDuringLoading, temperatureDuringLoading } = {}) {
  if (!measurements || !measurements.length) {
    return undefined
  }

  return measurements
    .filter(m => m && typeof m.volume === 'number' && typeof m.temperature === 'number')
    .map(m => {
      const density = calculateMeasurementDensity({
        densityDuringLoading,
        temperatureDuringLoading,
        temperatureMeasurement: m.temperature
      })

      return calculateMass({
        volume: m.volume,
        density
      })
    })
    .reduce(sum, 0)
}

export function calculateStartUnloading(unloadingDetails) {
  const unloadingStartTimes = (unloadingDetails || []).map(unloading => unloading.startTime).filter(time => time)
  if (!unloadingStartTimes || !unloadingStartTimes.length) {
    return ''
  }

  const earliestUnloadingStarttime = Math.min(...unloadingStartTimes)

  return moment(earliestUnloadingStarttime).format(startDateFormatString)
}

export function calculateEndUnloading(unloadingDetails) {
  const unloadingEndTimes = (unloadingDetails || []).map(unloading => unloading.endTime).filter(time => time)
  if (!unloadingEndTimes || !unloadingEndTimes.length) {
    return ''
  }

  const latestUnloadingEndtime = Math.max(...unloadingEndTimes)

  return moment(latestUnloadingEndtime).format(startDateFormatString)
}

export function calculateLevelDifference(startLevel, endLevel) {
  const startLevelNotEmpty = startLevel !== undefined && startLevel !== null && startLevel !== ''
  const endLevelNotEmpty = endLevel !== undefined && endLevel !== null && startLevel !== ''
  return startLevelNotEmpty && endLevelNotEmpty ? endLevel - startLevel : null
}

/**
 * Calculate the average unload speed in mt/h (megaton per hour)
 * Returns null if not all values are available in the unloading details
 * Returns the unload speed rounded in whole values
 *
 * @param {Object} values
 * @property {number} startTime
 * @property {number} endTime
 * @property {number} starLevel
 * @property {number} endLevel
 */
export function calculateAverageUnloadSpeed({ startTime, endTime, startLevel, endLevel } = {}) {
  if (
    typeof startTime !== 'number' ||
    typeof endTime !== 'number' ||
    typeof startLevel !== 'number' ||
    typeof endLevel !== 'number'
  ) {
    return null
  }

  const duration = endTime - startTime // milliseconds
  const durationHours = duration / 1000 / 60 / 60
  if (durationHours <= 0) {
    // this is physically impossible...
    return null
  }

  const unloadedAmountMT = endLevel - startLevel

  return unloadedAmountMT / durationHours
}

/**
 * Create a summary with all fields displayed in the "Summary" tab
 * of CargoOperationDetails. This information is also used to send an email
 * in case the vessel is blocked (ie. not all values are ok).
 *
 * The values in the summary are rounded and dates are formatted.
 *
 * @param {Order} order   The order of a TerminalVisit
 * @param {Object} cargoOperationsDetails Object with the filled in values
 *                 and measurements.
 * @param {Object} shipDetails Object with ship details, used for ENI, IMO, ship name.
 * @return {Object} Returns an object will all values like total measurements,
 *                  average temperature, etc.
 */
export function calculateSummary(order = { administrativeInfo: {} }, cargoOperationsDetails = {}, shipDetails = {}) {
  const {
    pNumber,
    bargeMeasurementDuringLoading,
    billOfLoadingWeight,
    billOfLoadingDate,
    measurements,
    approved,
    approvedBy,
    approvedTimestamp,
    statusComment
  } = cargoOperationsDetails

  const shipName = shipDetails.name || undefined
  const imo = shipDetails.imo || shipDetails.imoNumber || undefined
  const eni = shipDetails.eni || undefined
  const product = order.product

  const totalMeasuredVolume = calculateTotalLiters(measurements)
  const averageMeasuredTemperature = calculateAverageTemperature(cargoOperationsDetails)
  const totalMeasuredWeight = calculateTotalMass(cargoOperationsDetails)

  const weightDifferenceAtLoading =
    bargeMeasurementDuringLoading !== undefined && billOfLoadingWeight !== undefined
      ? (bargeMeasurementDuringLoading - billOfLoadingWeight) / billOfLoadingWeight
      : undefined

  const weightDifference =
    totalMeasuredWeight !== undefined && billOfLoadingWeight !== undefined
      ? (totalMeasuredWeight - billOfLoadingWeight) / billOfLoadingWeight
      : undefined

  const weightDifferenceIzegem =
    totalMeasuredWeight !== undefined && bargeMeasurementDuringLoading !== undefined
      ? (totalMeasuredWeight - bargeMeasurementDuringLoading) / bargeMeasurementDuringLoading
      : undefined

  const maxAllowedDifference = 0.002
  const weightDifferenceAtLoadingOk =
    weightDifferenceAtLoading !== undefined ? Math.abs(weightDifferenceAtLoading) <= maxAllowedDifference : undefined
  const weightDifferenceOk =
    weightDifference !== undefined ? Math.abs(weightDifference) <= maxAllowedDifference : undefined
  const weightDifferenceIzegemOk =
    weightDifferenceIzegem !== undefined ? Math.abs(weightDifferenceIzegem) <= maxAllowedDifference : undefined
  const allDifferencesOk =
    weightDifferenceOk !== undefined && weightDifferenceIzegemOk !== undefined
      ? weightDifferenceOk && weightDifferenceIzegemOk
      : undefined
  const statusOk = allDifferencesOk === true || approved === true

  return {
    // general
    dateOfArrival: formatDateIfDefined(order.cargoStart),
    shipName,
    imo,
    eni,
    pNumber,
    product,

    // loading
    billOfLoadingWeight,
    billOfLoadingDate: formatDateIfDefined(billOfLoadingDate),
    bargeMeasurementDuringLoading,
    weightDifferenceAtLoading: formatPercentageIfDefined(weightDifferenceAtLoading, 2),
    weightDifferenceAtLoadingOk,

    // Izegem
    totalMeasuredVolume,
    averageMeasuredTemperature: roundIfDefined(averageMeasuredTemperature, 1),
    totalMeasuredWeight: roundIfDefined(totalMeasuredWeight),
    weightDifference: formatPercentageIfDefined(weightDifference, 2),
    weightDifferenceOk,
    weightDifferenceIzegem: formatPercentageIfDefined(weightDifferenceIzegem, 2),
    weightDifferenceIzegemOk,
    allDifferencesOk,
    approved,
    approvedBy,
    approvedDate: formatDateIfDefined(approvedTimestamp),
    statusComment,
    statusOk
  }
}

/**
 * Round the value if it is a number
 * otherwise, leave the value untouched (like when it is null or undefined)
 * @param {*} value
 * @param {number} [precision=0]
 * @returns {*}
 */
export function roundIfDefined(value, precision = 0) {
  return typeof value === 'number' ? round(value, precision) : value
}

// helper function to format a date only if defined
export function formatDateIfDefined(date) {
  return date !== undefined && date !== null ? moment(date).format(startDateFormatString) : date
}

// helper function to format and round a number as percentage if defined
export function formatPercentageIfDefined(value, precision = 0) {
  return typeof value === 'number' ? round(value * 100, precision) + '%' : undefined
}

// helper function to calculate the sum of two numbers
function sum(a, b) {
  return a + b
}
